<template>
    <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200 tw-shadow-smx tw-max-w-sm">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h3 class="tw-text-base tw-font-semibold tw-text-gray-900">{{ $t('common.online') }} Users</h3>
            <div v-if="!loading" class="tw-flex tw-items-center tw-gap-2">
                <div class="tw-w-2 tw-h-2 tw-bg-green-500 tw-rounded-full tw-animate-pulse"></div>
                <span class="tw-text-xs tw-text-gray-600">{{ visibleUsers.length }}{{ online.length > maxVisible ? '+' : '' }}</span>
            </div>
            <div v-else class="tw-animate-pulse">
                <div class="tw-w-8 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="tw-space-y-2">
            <div v-for="i in 4" :key="i" class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-rounded-md tw-border tw-border-gray-100 tw-animate-pulse">
                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-full tw-flex-shrink-0"></div>
                <div class="tw-flex-1 tw-min-w-0 tw-space-y-1">
                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-3/4"></div>
                    <div class="tw-h-2 tw-bg-gray-200 tw-rounded tw-w-1/2"></div>
                </div>
                <div class="tw-w-1.5 tw-h-1.5 tw-bg-gray-200 tw-rounded-full"></div>
            </div>
        </div>

        <!-- Actual Content -->
        <div v-else-if="online.length" class="tw-space-y-2">
            <div class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-rounded-md tw-border tw-border-gray-100 hover:tw-bg-gray-50 tw-transition-colors"
                v-for="user in visibleUsers" :key="user.id">
                <!-- Profile Picture with Fallback -->
                <div class="tw-relative tw-flex-shrink-0">
                    <img 
                        v-if="user.profile_picture" 
                        :src="user.profile_picture" 
                        :alt="user.name"
                        @error="handleImageError"
                        class="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover tw-border tw-border-green-200"
                    />
                    <div 
                        v-else
                        class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gradient-to-br tw-from-primary-400 tw-to-primary-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-medium tw-text-xs tw-border tw-border-green-200"
                    >
                        {{ getInitials(user.name) }}
                    </div>
                    <!-- Online Status Indicator -->
                    <div class="tw-absolute -tw-bottom-0 -tw-right-0 tw-w-2.5 tw-h-2.5 tw-bg-green-500 tw-border tw-border-white tw-rounded-full"></div>
                </div>
                
                <div class="tw-flex-1 tw-min-w-0">
                    <p class="tw-text-xs tw-font-medium tw-text-gray-900 tw-truncate">{{ user.name }}</p>
                    <p class="tw-text-xs tw-text-gray-500 tw-truncate">{{ user.role || 'User' }}</p>
                </div>
                
                <!-- Activity Indicator -->
                <div class="tw-flex tw-items-center">
                    <div :class="[
                        'tw-w-1.5 tw-h-1.5 tw-rounded-full',
                        getActivityColor(user.activity)
                    ]"></div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="tw-text-center tw-py-6">
            <div class="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-2xl tw-text-gray-400" />
            </div>
            <p class="tw-text-gray-500 tw-text-sm">{{ $t('common.no-online-users') }}</p>
            <p class="tw-text-gray-400 tw-text-xs tw-mt-1">Users will appear here when they come online</p>
        </div>

        <!-- Show More Button -->
        <div v-if="!loading && online.length > maxVisible" class="tw-mt-3 tw-text-center">
            <button 
                @click="toggleShowAll" 
                class="tw-text-xs tw-text-primary-600 hover:tw-text-primary-700 tw-font-medium tw-transition-colors"
            >
                {{ showAll ? 'Show Less' : `+${online.length - maxVisible} more` }}
            </button>
        </div>

        
    </div>
</template>

<script setup>
import { useAuthStore, useUserStore } from '@/stores';
import { computed, ref, onMounted } from 'vue';

const userStore = useUserStore();
const authStore = useAuthStore()

// Loading state
const loading = ref(true)

// State for managing user display
const maxVisible = ref(4)
const showAll = ref(false)

// Online users without demo data
const online = computed(() => {
    return userStore.online.filter(u => u.id != authStore.user?.id)
})

// Computed for visible users based on showAll state
const visibleUsers = computed(() => {
    if (showAll.value) {
        return online.value
    }
    return online.value.slice(0, maxVisible.value)
})

// Methods for enhanced functionality
const toggleShowAll = () => {
    showAll.value = !showAll.value
}
const handleImageError = (event) => {
    // Hide the broken image and show initials fallback
    event.target.style.display = 'none'
    const nextElement = event.target.nextElementSibling
    if (nextElement) {
        nextElement.style.display = 'flex'
    }
}

const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
}

const getTimeOnline = (lastSeen) => {
    if (!lastSeen) return 'just now'
    
    const now = new Date()
    const diff = now - new Date(lastSeen)
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`
    
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    
    const days = Math.floor(hours / 24)
    return `${days}d ago`
}

getTimeOnline

const getActivityColor = (activity) => {
    switch (activity) {
        case 'active':
            return 'tw-bg-green-500'
        case 'away':
            return 'tw-bg-yellow-500'
        case 'busy':
            return 'tw-bg-red-500'
        case 'idle':
            return 'tw-bg-gray-400'
        default:
            return 'tw-bg-green-500'
    }
}

// Initialize component
onMounted(() => {
    setTimeout(() => {
        loading.value = false
    }, 1000)
})
</script>

<style></style>